import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { productService } from "../../../Api/Services/productService";
import { tenantFeaturedProductService } from "../../../Api/Services/tenantFeaturedProductService";
import { SimplifiedUserProduct } from "../../../Models/Product";
import { ProductCategory } from "../../../Models/ProductCategory";
import Navbar from "../../../Components/Navbar/Navbar";
import AltNavbar from "../../../Components/AltNavbar/AltNavbar";
import HeroCarousel from "../../../Components/HeroCarousel/HeroCarousel";
import ProductBar from "../../../Components/ProductBar/ProductBar";
import TrustBar from "../../../Components/TrustBar/TrustBar";
import Footer from "../../../Components/Footer/Footer";
import HomeSectionTitle from "../../../Components/HomeSectionTitle/HomeSectionTitle";

const UserHomePage = () => {
  const navigate = useNavigate();

  const { tenant, tenantPreference, categories } = useTenant();
  const altLayout = tenantPreference?.enableAltHomeLayout ?? false;

  const [products, setProducts] = useState<SimplifiedUserProduct[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<SimplifiedUserProduct[]>([]);
  const [firstCategory, setFirstCategory] = useState<ProductCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const bannerSlots = [
    { url: tenant.bannerImageOne?.url, mobilePositionX: tenantPreference?.bannerImage1MobilePositionX ?? null },
    { url: tenant.bannerImageTwo?.url, mobilePositionX: tenantPreference?.bannerImage2MobilePositionX ?? null },
    { url: tenant.bannerImageThree?.url, mobilePositionX: tenantPreference?.bannerImage3MobilePositionX ?? null },
    { url: tenant.bannerImageFour?.url, mobilePositionX: tenantPreference?.bannerImage4MobilePositionX ?? null },
  ].filter((slot): slot is { url: string; mobilePositionX: number | null } =>
    typeof slot.url === "string" && slot.url.trim().length > 0
  );
  const heroImages = bannerSlots.map(s => s.url);
  const heroMobilePositions = bannerSlots.map(s => s.mobilePositionX);

  useEffect(() => {
    const init = async () => {
      await loadProducts();
      if (altLayout) {
        await loadCategoryProducts();
      }
      setIsLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProducts = async () => {
    const featuredProducts = await tenantFeaturedProductService.getFeaturedProducts();
    if (featuredProducts.length > 0) {
      setProducts(altLayout ? featuredProducts.slice(0, 16) : featuredProducts);
      return;
    }
    const data = await productService.getAllActive({ page: 1, pageSize: altLayout ? 16 : 8 });
    setProducts(data.items);
  };

  const loadCategoryProducts = async () => {
    const filteredCats = categories.filter(
      (c) => !c.name.toLowerCase().includes("orçamento")
    );
    const cat = filteredCats[0] ?? null;
    if (!cat) {
      return;
    }
    setFirstCategory(cat);
    const data = await productService.getAllActive({ page: 1, pageSize: 16, categoryId: cat.id });
    setCategoryProducts(data.items);
  };


  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded" style={{ height, width }} />
  );

  const renderSkeleton = () => (
    <div className="space-y-10 px-6 py-12 sm:p-16">
      <SkeletonBlock height={32} width={200} />
      <SkeletonBlock height={18} width={260} />
      <SkeletonBlock height={8} width={80} />
      <SkeletonBlock height={240} />
      <SkeletonBlock height={32} width={220} />
      <SkeletonBlock height={18} width={260} />
      <SkeletonBlock height={8} width={80} />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBlock key={i} height={80} />
        ))}
      </div>
      <SkeletonBlock height={120} />
    </div>
  );

  // ─── ALT LAYOUT ────────────────────────────────────────────────
  if (altLayout) {
    const filteredCategories = categories.filter(
      (c) => !c.name.toLowerCase().includes("orçamento")
    );
    const toTitleCase = (s: string) =>
      s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

    return (
      <div>
        {/* Hero banner — navbar floats transparently over it */}
        {heroImages.length > 0 ? (
          <div className="relative">
            <AltNavbar transparent={true} />
            {/* Gradient overlay so nav text stays readable on any banner */}
            <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black/40 to-transparent z-30 pointer-events-none" />
            <HeroCarousel images={heroImages} className="h-[82vh] sm:h-[85vh]" imagePosition="object-[15%_center]" mobileImagePositions={heroMobilePositions} />
          </div>
        ) : (
          <AltNavbar />
        )}

        {isLoading ? (
          renderSkeleton()
        ) : (
          <>
            {/* Featured products */}
            {products.length > 0 && (
              <div className="px-6 py-12 sm:px-0 sm:py-16">
                <HomeSectionTitle title="PRODUTOS" subtitle="Confira alguns dos nossos produtos" />
                <div className="-mx-6 sm:mx-0">
                  <ProductBar products={products} altLayout={true} />
                </div>
              </div>
            )}

            {/* Categories grid */}
            {filteredCategories.length > 0 && (
              <div className="page-gutter py-12 sm:py-16">
                <HomeSectionTitle title="CATEGORIAS" subtitle="Confira os produtos das seguintes categorias" dividerClassName="mb-10" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => navigate(`/produtos?categoria=${category.id}`)}
                      className="bg-primary-600 hover:bg-primary-700 p-4 h-20 flex items-center justify-center transition rounded-md shadow-sm text-base font-semibold text-neutral-900"
                    >
                      {toTitleCase(category.name)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Products section after categories — first category */}
            {categoryProducts.length > 0 && firstCategory && (
              <div className="px-6 py-12 sm:px-0 sm:py-16">
                <HomeSectionTitle
                  title={firstCategory.name.toUpperCase()}
                  subtitle={`Confira alguns dos nossos ${firstCategory.name.toLowerCase()}`}
                />
                <div className="-mx-6 sm:mx-0">
                  <ProductBar products={categoryProducts} altLayout={true} />
                </div>
              </div>
            )}

            <TrustBar />
            <Footer />
          </>
        )}
      </div>
    );
  }

  // ─── CLASSIC LAYOUT (unchanged) ────────────────────────────────
  return (
    <div>
      <Navbar />

      {tenantPreference?.homeBannerPlacement === "top" && heroImages.length > 0 && (
        <HeroCarousel images={heroImages} mobileImagePositions={heroMobilePositions} />
      )}

      {isLoading ? (
        renderSkeleton()
      ) : (
        <>
          {products && products.length > 0 && (
            <div className="px-6 pt-16 pb-12 sm:p-16">
              <HomeSectionTitle title="PRODUTOS" subtitle="Confira alguns dos nossos produtos" />
              <div className="-mx-6 sm:mx-0">
                <ProductBar products={products} />
              </div>
            </div>
          )}

          {tenant?.initialImageOne && tenant?.initialImageTwo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 page-gutter">
              <img src={tenant.initialImageOne.url} alt="Imagem 1" className="w-full object-cover h-64 sm:h-80 shadow-md" />
              <img src={tenant.initialImageTwo.url} alt="Imagem 2" className="w-full object-cover h-64 sm:h-80 shadow-md" />
            </div>
          )}

          {products && products.length > 0 && (
            <div className="page-gutter py-16 sm:py-24">
              <HomeSectionTitle
                title="CATEGORIAS"
                subtitle="Confira os produtos das seguintes categorias"
                dividerClassName="mb-12 sm:mb-14"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => navigate(`/produtos?categoria=${category.id}`)}
                    className="border p-4 h-20 flex items-center justify-center transition rounded-md shadow-sm hover:bg-primary-50"
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tenantPreference?.homeBannerPlacement === "bottom" && heroImages.length > 0 && (
            <HeroCarousel images={heroImages} mobileImagePositions={heroMobilePositions} />
          )}

          <TrustBar />
          <Footer />
        </>
      )}
    </div>
  );
};

export default UserHomePage;
